import { Action, combineReducers, configureStore, Reducer } from "@reduxjs/toolkit";
import {
  PersistConfig,
  PersistState,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { userReducer } from "@/auth/state/userSlice";
import { utilReducer } from "@/utils/state/utilSlice";

// redux-persist can't handle Partial<RootState> as preloadedState.
// redux-persist is no longer maintain so probably won't get this update. Here is a workaround
declare module "redux-persist" {
  export function persistReducer<S, A extends Action = Action, P = S>(
    config: PersistConfig<S>,
    baseReducer: Reducer<S, A, P>,
  ): Reducer<S & { _persist: PersistState }, A, P & { _persist?: PersistState }>;
}

const persistConfig: PersistConfig<RootState> = {
  key: "root",
  storage,
  version: 1,
  blacklist: ["util"],
};

export const rootReducer = combineReducers({
  user: userReducer,
  util: utilReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
